import { string, z } from "zod";
import { zStringDate, zStringId } from "../../utils";
import {
  ProfileCompanyTypes,
  ProfileEmployeePoolSize,
  ProfileExperienceLevelEnum,
  ProfileGenderEnum,
  ProfileLanguageProficiency,
  ProfileTemplatesEnum,
  ProfileTypesEnum,
} from "./profile-statics";
import { zLocation } from "../users/user-resource";
import { USER_ROLES_ENUM } from "../users";

const zProfileCommonFields = {
  _id: zStringId(),

  userDoc: z.object({
    _id: zStringId(),
    firstName: z.string(),
    lastName: z.string().optional(),
    username: z.string(),
    country: z.string(),
    profileImage: z.string(),

    roleDoc: z.object({
      _id: zStringId(),
      name: z.nativeEnum(USER_ROLES_ENUM),
    }),

    primaryAddress: zLocation.optional(),
    billingAddress: zLocation.optional(),
    timezone: z.string().optional(),

    email: z.string(),
    phoneNumber: z.object({ code: z.string(), number: z.string() }).optional(),

    stripeCards: z
      .object({
        id: z.string().optional(),
        customer: z.string().optional(),
        exp_month: z.string().optional(),
        exp_year: z.string().optional(),
        brand: z.string().optional(),
        last4: z.string().optional(),
        isPrimary: z.boolean(),
        isVerified: z.boolean(),
      })
      .array(),
    stripeCustCognifleId: z.string().optional(),
    paypalAccId: z.string().array(),

    isDocumentVerified: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    membership: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
    membershipBenefits: z.object({
      membershipServiceSlots: z.number(),
      purchasedServiceSlots: z.number(),
      eligibleForFreeTrial: z.boolean(),
    }),

    visibleOnProfile: z
      .object({
        phone: z.boolean(),
        email: z.boolean(),
      })
      .optional(),

    twoFactorAuth: z.object({
      email: z.boolean(),
      sms: z.boolean(),
    }),
    securityQuestions: z
      .object({
        Questions: z.string().optional(),
        // Answer: z.string().optional(),
      })
      .array(),

    notificationSettings: z.object({
      accountActivity: z.object({ email: z.boolean(), push: z.boolean() }),
      projectUpdates: z.object({ email: z.boolean(), push: z.boolean() }),
      reviewsAndRatings: z.object({ email: z.boolean(), push: z.boolean() }),
      featured: z.object({ email: z.boolean(), push: z.boolean() }),
      news: z.object({ email: z.boolean(), push: z.boolean() }),
      promotions: z.object({ email: z.boolean(), push: z.boolean() }),
    }),

    fieldUpdates: z
      .object({
        password: z
          .object({
            updatedAt: zStringDate().optional(),
          })
          .optional(),
      })
      .optional(),
  }),

  selectedProfileTemplate: z.nativeEnum(ProfileTemplatesEnum).optional(),
  isOnboardingComplete: z.boolean(),
  isComplete: z.boolean(),
  // onHomepage: z.boolean(),

  primaryLocation: zLocation.optional(),
  languages: z
    .object({
      languageName: z.string(),
      proficiency: z.nativeEnum(ProfileLanguageProficiency),
    })
    .array(),
  updatedAt: zStringDate(),
  createdAt: zStringDate(),
};

const zProfileCompanyInfoFields = {
  legalName: z.string(),
  dba: z.string().optional(),
  about: z.string(),
};

const zProfileCompanyOverviewFields = {
  companyType: z.nativeEnum(ProfileCompanyTypes),
  numberOfEmployees: z.nativeEnum(ProfileEmployeePoolSize),
};

const zProfileCommonProviderFields = {
  experienceLevel: z.nativeEnum(ProfileExperienceLevelEnum).optional(),
  linkedAccounts: z
    .object({
      accountOn: z.string(),
      profileLink: z.string(),
    })
    .array(),
  profileCategory: z
    .object({
      categoryDoc: z.object({
        _id: zStringId(),
        name: string(),
      }),
      subCategoryDoc: z.object({
        _id: zStringId(),
        name: string(),
      }),
      skillsIds: z.string().array(),
    })
    .optional(),
};

export const zProfileOfClient = z.object({
  profileType: z.literal(ProfileTypesEnum.CLIENT),
  ...zProfileCommonFields,
});

export const zProfileOfCompany = z.object({
  profileType: z.literal(ProfileTypesEnum.COMPANY),
  ...zProfileCommonFields,

  availableOn: z
    .object({
      name: z.string(),
    })
    .array()
    .optional(),

  companyInfo: z.object({ ...zProfileCompanyInfoFields }).optional(),
  companyOverview: z
    .object({
      ...zProfileCompanyOverviewFields,
      representativeTitle: z.string().optional(),
    })
    .optional(),
});

export const zProfileOfProfessional = z.object({
  profileType: z.literal(ProfileTypesEnum.PROFESSIONAL),
  ...zProfileCommonFields,
  ...zProfileCommonProviderFields,
  title: z.string().optional(),
  overview: z.string().optional(),
  cvLink: z.string().optional(),
  workHistory: z
    .object({
      company: z.string(),
      jobTitle: z.string(),
      startAt: zStringDate(),
      endAt: zStringDate().optional(),
      isWorking: z.boolean().optional(),
      description: z.string().optional(),
      location: z.object({
        city: z.string(),
        country: z.string(),
      }),
    })
    .array(),
  educationHistory: z
    .object({
      college: z.string(),
      area: z.string().optional(),
      degree: z.string().optional(),
      startAt: zStringDate().optional(),
      endAt: zStringDate().optional(),
      currentlyStudying: z.boolean().optional(),
      description: z.string().optional(),
    })
    .array(),
});

export const zProfileOfBusiness = z.object({
  profileType: z.literal(ProfileTypesEnum.BUSINESS),
  ...zProfileCommonFields,
  ...zProfileCommonProviderFields,
  secondaryLocation: zLocation.optional(),
  trustedBy: z
    .object({
      name: z.string(),
    })
    .array(),

  companyInfo: z
    .object({
      ...zProfileCompanyInfoFields,
      licenseNumber: z.string(),
    })
    .optional(),

  companyOverview: z
    .object({
      ...zProfileCompanyOverviewFields,
      industryId: zStringId(),
      establishedYear: zStringDate(),
    })
    .optional(),
  representativeInfo: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      title: z.string(),
      gender: z.nativeEnum(ProfileGenderEnum),
      dob: zStringDate(),
      country: z.string(),
      phoneNumber: z.string(),
    })
    .optional(),
  qualifications: z
    .object({
      title: z.string(),
      issuedAt: zStringDate(),
      expiresAt: zStringDate().optional(),
      certificateLink: z.string(),
    })
    .array(),
});

export const zProfile = z.union([
  zProfileOfClient,
  zProfileOfCompany,
  zProfileOfProfessional,
  zProfileOfBusiness,
]);

export type Profile = z.infer<typeof zProfile>;
export type ProfileOfClient = z.infer<typeof zProfileOfClient>;
export type ProfileOfCompany = z.infer<typeof zProfileOfCompany>;
export type ProfileOfProfessional = z.infer<typeof zProfileOfProfessional>;
export type ProfileOfBusiness = z.infer<typeof zProfileOfBusiness>;
